"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader, Send, PawPrint, CircleCheck } from "lucide-react";
const floatingInputClass = "peer h-14 px-4 pt-5 pb-2 w-full rounded-md border border-input bg-transparent text-base shadow-xs transition-colors placeholder:text-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm";
const floatingLabelClass = "absolute left-4 top-4 -translate-y-1/2 text-muted-foreground transition-all peer-placeholder-shown:top-1/2 peer-placeholder-shown:text-base peer-focus:top-4 peer-focus:text-xs peer-focus:text-primary pointer-events-none data-[error=true]:text-destructive data-[error=true]:peer-focus:text-destructive";
function EnrollmentFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-lg bg-accent/20 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-ring" />
        </div>
        <h3 className="text-2xl font-default font-semibold text-foreground">Assessment Received</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-ring text-ring hover:bg-accent">
          Start New Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="firstName" rules={{
        required: "First name is required"
      }} render={({
        field
      }) => <FormItem className="relative space-y-0">
              <FormControl>
                <Input className={floatingInputClass} placeholder="First Name" {...field} />
              </FormControl>
              <FormLabel className={floatingLabelClass}>First Name</FormLabel>
              <FormMessage className="pt-1" />
            </FormItem>} />

        <FormField control={control} name="lastName" rules={{
        required: "Last name is required"
      }} render={({
        field
      }) => <FormItem className="relative space-y-0">
              <FormControl>
                <Input className={floatingInputClass} placeholder="Last Name" {...field} />
              </FormControl>
              <FormLabel className={floatingLabelClass}>Last Name</FormLabel>
              <FormMessage className="pt-1" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email format"
        }
      }} render={({
        field
      }) => <FormItem className="relative space-y-0">
              <FormControl>
                <Input type="email" className={floatingInputClass} placeholder="Email" {...field} />
              </FormControl>
              <FormLabel className={floatingLabelClass}>Email Address</FormLabel>
              <FormMessage className="pt-1" />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem className="relative space-y-0">
              <FormControl>
                <Input type="tel" className={floatingInputClass} placeholder="Phone" {...field} />
              </FormControl>
              <FormLabel className={floatingLabelClass}>Phone Number</FormLabel>
              <FormMessage className="pt-1" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="company" rules={{
        required: "Company name is required"
      }} render={({
        field
      }) => <FormItem className="relative space-y-0">
              <FormControl>
                <Input className={floatingInputClass} placeholder="Company" {...field} />
              </FormControl>
              <FormLabel className={floatingLabelClass}>Company Name</FormLabel>
              <FormMessage className="pt-1" />
            </FormItem>} />

        <FormField control={control} name="industry" render={({
        field
      }) => <FormItem className="relative space-y-0">
              <FormControl>
                <Input className={floatingInputClass} placeholder="Industry" {...field} />
              </FormControl>
              <FormLabel className={floatingLabelClass}>Industry</FormLabel>
              <FormMessage className="pt-1" />
            </FormItem>} />
      </div>

      <FormField control={control} name="employeeCount" rules={{
      required: "Please select company size"
    }} render={({
      field
    }) => <FormItem className="relative space-y-0">
            <FormLabel className="absolute left-4 top-2 text-xs text-muted-foreground z-10 pointer-events-none">
              Employee Count
            </FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="h-14 pt-5 pb-2 px-4 w-full bg-transparent border-input focus:ring-2 focus:ring-ring">
                  <SelectValue placeholder="Select company size" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="1-10">1-10 Employees</SelectItem>
                <SelectItem value="11-50">11-50 Employees</SelectItem>
                <SelectItem value="51-200">51-200 Employees</SelectItem>
                <SelectItem value="201-500">201-500 Employees</SelectItem>
                <SelectItem value="500+">500+ Employees</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage className="pt-1" />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem className="relative space-y-0">
            <FormControl>
              <textarea {...field} className="peer flex w-full rounded-md border border-input bg-transparent px-4 pt-6 pb-2 text-base shadow-xs transition-colors placeholder:text-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm min-h-[120px] resize-y" placeholder="Workflow Challenge" />
            </FormControl>
            <FormLabel className="absolute left-4 top-6 -translate-y-1/2 text-muted-foreground transition-all peer-placeholder-shown:top-6 peer-placeholder-shown:text-base peer-focus:top-4 peer-focus:text-xs peer-focus:text-primary pointer-events-none data-[error=true]:text-destructive data-[error=true]:peer-focus:text-destructive">
              Describe your primary workflow challenge
            </FormLabel>
            <FormMessage className="pt-1" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
          {submitError}
        </div>}

      <Button type="submit" size="lg" disabled={isSubmitting} className="w-full sm:w-auto bg-ring hover:bg-secondary text-white transition-all duration-200">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing...
          </> : <>
            <Send className="mr-2 size-5" />
            Submit Assessment
          </>}
      </Button>
    </Form>;
}
export default function StartYourAssessment() {
  return <section data-section-id="259162" id="start-your-assessment" className="py-16 md:py-24 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10 pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-lg bg-ring/5 blur-3xl" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-lg bg-secondary/5 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {}
          <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-24">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-accent text-ring text-sm font-medium">
                <PawPrint className="size-4" />
                <span>AI Readiness Assessment</span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground leading-tight">
                Turn AI Into Measurable Business Outcomes
              </h2>
              <p className="text-lg text-muted-foreground font-default">
                We help businesses identify high-value workflows, enable employees, automate repetitive work, and build a scalable AI operating model.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 mt-1">
                  <div className="size-10 rounded-lg bg-accent flex items-center justify-center text-ring font-bold">
                    1
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-default font-semibold text-foreground">Identify Workflows</h4>
                  <p className="text-muted-foreground mt-1">Tell us about your current processes and where you see the most friction in your daily operations.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 mt-1">
                  <div className="size-10 rounded-lg bg-accent flex items-center justify-center text-ring font-bold">
                    2
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-default font-semibold text-foreground">Expert Analysis</h4>
                  <p className="text-muted-foreground mt-1">Our consultants analyze your data to propose a custom AI Operating System tailored to your SMB needs.</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="flex-shrink-0 mt-1">
                  <div className="size-10 rounded-lg bg-accent flex items-center justify-center text-ring font-bold">
                    3
                  </div>
                </div>
                <div>
                  <h4 className="text-xl font-default font-semibold text-foreground">Measurable Impact</h4>
                  <p className="text-muted-foreground mt-1">Begin implementation and track hours saved, cost reductions, and productivity gains in real-time.</p>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Card className="border-border shadow-lg bg-background/80 backdrop-blur-sm rounded-lg">
              <CardHeader className="pb-6 border-b border-border">
                <CardTitle className="text-2xl font-default">Connect With Our Experts</CardTitle>
                <CardDescription className="font-default">
                  Your information is secure and will only be used to provide your personalized AI readiness report.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-6">
                <FormProvider formKey="start_assessment_ai_readiness" formId="start_assessment_ai_readiness" sectionName="Start Your Assessment" defaultValues={{
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
                company: "",
                industry: "",
                employeeCount: "",
                message: ""
              }} submitText="Submit Assessment" successMessage="Thank you for your inquiry! Our consulting team will review your assessment and contact you within 24 hours.">
                  <EnrollmentFormContent />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}